/*
 * cc-audio-file — audio player custom element for the "CC Audio File" theme block.
 * Reads config from the host element's data attributes:
 *   data-skip            seconds for the -/+ skip buttons
 *   data-duration-label  placeholder shown until real length loads
 *
 * The waveform bar count is derived from the track's duration once its
 * metadata loads, so longer files render a denser waveform automatically.
 */
(function () {
  if (customElements.get('cc-audio-file')) return;

  var fmt = function (s, pad) {
    if (!isFinite(s) || s < 0) s = 0;
    var m = Math.floor(s / 60);
    var ss = Math.floor(s % 60);
    var mm = pad ? String(m).padStart(2, '0') : String(m);
    return mm + ':' + String(ss).padStart(2, '0');
  };

  // Deterministic pseudo-random so the waveform is stable across renders.
  var rand = function (i) {
    var x = Math.sin((i + 1) * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };

  // Roughly one bar per ~0.7s of audio, clamped so very short/long files
  // still look sensible. Falls back to 90 when the duration is unknown.
  var BARS_PER_SECOND = 1.4;
  var MIN_BARS = 40;
  var MAX_BARS = 200;
  var barsForDuration = function (d) {
    if (!isFinite(d) || d <= 0) return 90;
    return Math.min(MAX_BARS, Math.max(MIN_BARS, Math.round(d * BARS_PER_SECOND)));
  };

  class CCAudioFile extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.audio = this.querySelector('.cc-audio__el');
      this.wave = this.querySelector('.cc-audio__wave');
      this.playBtn = this.querySelector('.cc-audio__play');
      this.current = this.querySelector('.cc-audio__time--current');
      this.total = this.querySelector('.cc-audio__time--total');
      if (!this.audio || !this.wave || !this.playBtn) return;

      this.skip = parseFloat(this.dataset.skip) || 10;
      // Provisional waveform; rebuilt once the real duration is known.
      this.buildBars(barsForDuration(this.audio.duration));
      this.bind();
    }

    buildBars(count) {
      if (this.bars && this.bars.length === count) return;
      this.wave.innerHTML = '';
      this.bars = [];
      var frag = document.createDocumentFragment();
      for (var i = 0; i < count; i++) {
        var envelope = 0.3 + 0.7 * Math.sin((i / count) * Math.PI);
        var h = Math.max(0.1, rand(i) * envelope);
        var bar = document.createElement('span');
        bar.className = 'cc-audio__bar';
        bar.style.height = (h * 100).toFixed(1) + '%';
        frag.appendChild(bar);
        this.bars.push(bar);
      }
      this.wave.appendChild(frag);
    }

    bind() {
      var self = this;

      this.playBtn.addEventListener('click', function () {
        self.audio.paused ? self.audio.play() : self.audio.pause();
      });

      this.querySelectorAll('.cc-audio__skip').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = parseFloat(btn.dataset.dir) || 1;
          var d = self.audio.duration || 0;
          self.audio.currentTime = Math.min(Math.max(0, self.audio.currentTime + dir * self.skip), d || self.audio.currentTime);
        });
      });

      this.wave.addEventListener('click', function (e) {
        var rect = self.wave.getBoundingClientRect();
        var ratio = Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1);
        if (self.audio.duration) self.audio.currentTime = ratio * self.audio.duration;
      });

      this.wave.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var dir = e.key === 'ArrowRight' ? 1 : -1;
          self.audio.currentTime = Math.max(0, self.audio.currentTime + dir * 5);
        }
      });

      this.audio.addEventListener('play', function () {
        self.playBtn.classList.add('is-playing');
        self.playBtn.setAttribute('aria-label', 'Pause');
        document.querySelectorAll('cc-audio-file .cc-audio__el').forEach(function (el) {
          if (el !== self.audio) el.pause();
        });
      });

      this.audio.addEventListener('pause', function () {
        self.playBtn.classList.remove('is-playing');
        self.playBtn.setAttribute('aria-label', 'Play');
      });

      this.audio.addEventListener('loadedmetadata', function () {
        self.buildBars(barsForDuration(self.audio.duration));
        self.render();
        if (self.total && isFinite(self.audio.duration)) {
          self.total.textContent = fmt(self.audio.duration, false);
        }
      });

      this.audio.addEventListener('timeupdate', function () {
        self.render();
      });

      this.audio.addEventListener('ended', function () {
        self.audio.currentTime = 0;
      });
    }

    render() {
      var d = this.audio.duration || 0;
      var progress = d ? this.audio.currentTime / d : 0;
      if (this.current) this.current.textContent = fmt(this.audio.currentTime, true);

      var played = Math.round(progress * this.bars.length);
      for (var i = 0; i < this.bars.length; i++) {
        this.bars[i].classList.toggle('is-played', i < played);
      }
      this.wave.setAttribute('aria-valuenow', Math.round(progress * 100));
    }
  }

  customElements.define('cc-audio-file', CCAudioFile);
})();
